import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { Skeleton } from '../../../ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../ui/tooltip';
import { cn } from '../../../ui/utils';
import { pub } from './messages';
import type { CardMenu } from '../shared/PostMenu';
import { PostAvatar, PostAuthor, StoryCaptured, StoryFrame, postAge } from './cells';
import { PostCard, shapeOf, type CardShape } from './PostCard';
import { isStory, type PostEntry } from './readings';
import { getLocale } from '../../../i18n';
import { Badge } from '../../../ui/badge';

/** Justified rows: each card takes a width weighted by its shape and its own
 *  content's height (`items-start`), so no card stretches to a taller
 *  neighbour; a short last row stays centred. One column on a phone, two cards
 *  a row from 36rem of feed, three or four from 64rem. DOM order is the
 *  reading order. */
const ROWS = 'flex flex-wrap items-start justify-center gap-3';

/** A day's stories as one row of dense 9:16 tiles, scrolled sideways: two in
 *  view on a phone, four from 36rem of feed, six from 64rem. */
const TILES = 'flex snap-x gap-2 overflow-x-auto pb-2';
const TILE = 'feed-tile';
const LANE = 'min-w-0 basis-full';
const SHAPE: Record<CardShape, string> = {
  post: 'feed-shape-post',
  tall: 'feed-shape-tall',
  wide: 'feed-shape-wide',
};
const SKELETON_CARDS = 3;

function FeedItem(props: { entry: PostEntry; href?: string; menu?: CardMenu }): ReactNode {
  const [aspect, setAspect] = useState<number | null>(null);
  const shape = shapeOf(props.entry, aspect);
  return (
    <li className={cn(LANE, SHAPE[shape])}>
      <PostCard
        entry={props.entry}
        href={props.href}
        menu={props.menu}
        shape={shape}
        onAspect={setAspect}
      />
    </li>
  );
}

/** A story as a compact tile: its frame filling the tile, the author, age and
 *  whether it is still up over a scrim, and when it was captured on hover and
 *  focus. */
function StoryTile(props: { entry: PostEntry; href?: string }): ReactNode {
  const { post } = props.entry;
  const expired = post.expires_at !== null && Date.parse(post.expires_at) <= Date.now();
  const overlay = (
    <>
      <StoryFrame post={post} className="absolute inset-0 aspect-auto size-full" />
      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-1.5 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-2 pt-8 text-white">
        <span className="flex min-w-0 items-center gap-1.5">
          <PostAvatar post={post} />
          <span className="text-caption truncate font-medium">
            <PostAuthor post={post} />
          </span>
        </span>
        <span className="text-caption flex items-center justify-between gap-1 text-white/80">
          {postAge(post.published_at, getLocale())}
          <Badge variant={expired ? 'secondary' : 'default'}>
            {pub(expired ? 'story.expired' : 'story.active')}
          </Badge>
        </span>
      </div>
    </>
  );
  const tileClass =
    'focus-visible:ring-ring relative block aspect-story overflow-hidden rounded-lg bg-black outline-none focus-visible:ring-3';
  const tile =
    props.href !== undefined ? (
      <Link to={props.href} data-post={post.post_id} className={tileClass}>
        {overlay}
      </Link>
    ) : (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the capture time's tooltip must reach keyboard users
      <div tabIndex={0} data-post={post.post_id} className={tileClass}>
        {overlay}
      </div>
    );
  if (post.captured_at === null) return <li className={TILE}>{tile}</li>;
  return (
    <li className={TILE}>
      <Tooltip>
        <TooltipTrigger asChild>{tile}</TooltipTrigger>
        <TooltipContent>
          <StoryCaptured capturedAt={post.captured_at} />
        </TooltipContent>
      </Tooltip>
    </li>
  );
}

type Run = { stories: false; entry: PostEntry } | { stories: true; entries: PostEntry[] };

/** Each day's stories fold into one tile row that opens that day, so a story
 *  never takes a post card's footprint and the day's posts flow as full rows
 *  under it. */
function runsOf(entries: PostEntry[]): Run[] {
  const runs: Run[] = [];
  const strips = new Map<string, PostEntry[]>();
  for (const entry of entries) {
    const day = new Date(entry.post.published_at).toDateString();
    let strip = strips.get(day);
    if (strip === undefined) {
      strip = [];
      strips.set(day, strip);
      runs.push({ stories: true, entries: strip });
    }
    if (isStory(entry.post)) strip.push(entry);
    else runs.push({ stories: false, entry });
  }
  return runs.filter((run) => !run.stories || run.entries.length > 0);
}

export function PostFeed(props: {
  entries: PostEntry[];
  hrefFor?: (entry: PostEntry) => string;
  menu?: CardMenu;
}): ReactNode {
  return (
    <div className="@container">
      <ul className={ROWS}>
        {runsOf(props.entries).map((run) =>
          run.stories ? (
            <li key={`stories-${run.entries[0]!.post.post_id}`} className="w-full">
              <ul aria-label={pub('kindFilter.story')} className={TILES}>
                {run.entries.map((entry) => (
                  <StoryTile key={entry.post.post_id} entry={entry} href={props.hrefFor?.(entry)} />
                ))}
              </ul>
            </li>
          ) : (
            <FeedItem
              key={run.entry.post.post_id}
              entry={run.entry}
              href={props.hrefFor?.(run.entry)}
              menu={props.menu}
            />
          ),
        )}
      </ul>
    </div>
  );
}

export function PostFeedSkeleton(): ReactNode {
  return (
    <div className="@container">
      <div className={ROWS} role="status">
        <span className="sr-only">{pub('loading.feed')}</span>
        {Array.from({ length: SKELETON_CARDS }, (_, index) => (
          <Skeleton key={index} aria-hidden className={cn(LANE, SHAPE.post, 'h-96')} />
        ))}
      </div>
    </div>
  );
}
